// frontend/src/components/document-explorer/FolderTree.js
// Recursive, expandable folder tree for DocumentExplorer.js's left pane.
// Shared by case mode (folders under one matter) and client mode (matters +
// their folders, plus a virtual Unfiled bucket) - both normalize into the
// same node shape before reaching this component:
//   {
//     id: 'folder-42' | 'matter-17' | 'client-unfiled',
//     type: 'folder' | 'matter' | 'unfiled',
//     name, isVirtual, raw, caseId,
//     children: [...same shape...],
//     hasChildren: boolean,
//   }
import React from 'react';

const TYPE_ICON = {
  matter: 'bi-folder2',
  unfiled: 'bi-folder-x',
};

function FolderRow({ node, depth, selectedFolderId, expandedIds, onToggleExpand, onOpenFolder, onDropDocument, onDropFolder }) {
  const isExpanded = expandedIds.has(node.id);
  const isSelected = selectedFolderId === node.id;
  const hasChildren = node.hasChildren || (node.children && node.children.length > 0);
  const icon = TYPE_ICON[node.type] || (isSelected ? 'bi-folder2-open' : 'bi-folder');

  return (
    <div>
      <div
        onClick={() => onOpenFolder(node)}
        draggable={node.type === 'folder'}
        onDragStart={(e) => {
          if (node.type !== 'folder') return;
          e.dataTransfer.setData('application/x-folder-id', String(node.raw.id));
        }}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const draggedFolderId = e.dataTransfer.getData('application/x-folder-id');
          if (draggedFolderId) {
            onDropFolder(draggedFolderId, node);
            return;
          }
          const docId = e.dataTransfer.getData('text/plain');
          if (docId) onDropDocument(docId, node);
        }}
        className={`p-2 rounded cursor-pointer transition-colors flex items-center gap-1.5 ${
          isSelected ? 'bg-blue-100 text-blue-700 font-medium' : 'hover:bg-gray-100 text-gray-700'
        }`}
        style={{ paddingLeft: `${8 + depth * 16}px` }}
      >
        {hasChildren ? (
          <i
            className={`bi ${isExpanded ? 'bi-chevron-down' : 'bi-chevron-right'} text-gray-400 shrink-0`}
            style={{ fontSize: '0.65rem', width: '0.9rem' }}
            onClick={(e) => { e.stopPropagation(); onToggleExpand(node.id); }}
          ></i>
        ) : (
          <span style={{ width: '0.9rem' }} className="shrink-0"></span>
        )}
        <i className={`bi ${icon} shrink-0`}></i>
        <span className="text-sm truncate">{node.name}</span>
      </div>
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <FolderRow
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedFolderId={selectedFolderId}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              onOpenFolder={onOpenFolder}
              onDropDocument={onDropDocument}
              onDropFolder={onDropFolder}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default function FolderTree({ nodes, selectedFolderId, expandedIds, onToggleExpand, onOpenFolder, onDropDocument, onDropFolder }) {
  return (
    <div className="h-full bg-gray-50 border-r border-gray-200">
      <div className="p-3 border-b border-gray-200 bg-white">
        <h3 className="font-semibold text-gray-700 flex items-center gap-2">
          <i className="bi bi-folder2-open"></i>
          Folders
        </h3>
      </div>
      <div className="p-2 space-y-0.5 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 120px)' }}>
        {(!nodes || nodes.length === 0) ? (
          <div className="text-center text-gray-500 py-4 text-sm">No folders</div>
        ) : (
          nodes.map((node) => (
            <FolderRow
              key={node.id}
              node={node}
              depth={0}
              selectedFolderId={selectedFolderId}
              expandedIds={expandedIds}
              onToggleExpand={onToggleExpand}
              onOpenFolder={onOpenFolder}
              onDropDocument={onDropDocument}
              onDropFolder={onDropFolder}
            />
          ))
        )}
      </div>
    </div>
  );
}
